import React, { useState, useEffect } from "react";
import useRTLContext from "../../../../hooks/useRTLContext";
import { useTranslation } from "react-i18next";
import { Edit, Eye } from "lucide-react";
import MainServices from "../../../../services/MainServices";
import { notifyWarn } from "../../../../assets/elements/Toast";
import useAuthContext from "../../../../hooks/useAuthContext";
import AddEmployeeModal from "../employeesModals/AddEmployeeModal";
import DeleteConfirmationModal from "../table/DeleteModal";
import ExclaimRed from "../../../../assets/customIcons/ExclaimRed";
import Tick from "../../../../assets/customIcons/Tick";
import EmployeeModal from "../employeesNew/EmployeesMain";
import useCurrentUserContext from "../../../../hooks/useCurrentUserContext";

const MyProfile = () => {
  const { setCurrentUserChange } = useCurrentUserContext();
  const { getEmployeesRegistrationById } = MainServices();
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addEmployeeModal, setAddEmployeeModal] = useState(false);
  const [profileModal, setProfileModal] = useState(false);
  const [viewProfileData, setViewProfileData] = useState("");
  const [viewProfileEmpPublicId, setViewProfileEmpPublicId] = useState("");
  const [editData, setEditData] = useState(null);
  const { isRTL, direction } = useRTLContext();
  const { t } = useTranslation();
  const { userId } = useAuthContext();
  const [notifySuccessModal, setNotifySuccessModal] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmMsg, setConfirmMsg] = useState("");
  const [confirmBtn, setConfirmBtn] = useState("");
  const [submitTitle, setSubmitTitle] = useState("");
  const [submitMsg, setSubmitMsg] = useState("");
  const [submitBtn, setSubmitBtn] = useState("");
  const [notifyWarnModal, setNotifyWarnModal] = useState(false);

  // Fetch profile data
  useEffect(() => {
    getItemsMaster();
  }, []);

  const getItemsMaster = async () => {
    console.log(
      "getting table data: ",
      viewProfileData,
      viewProfileEmpPublicId
    );
    setIsLoading(true);

    try {
      const response = await getEmployeesRegistrationById(userId);
      if (response?.data) {
        setIsLoading(false);
        const data = response?.data?.data;
        setProfileData(data);
        setCurrentUserChange(
          `${data?.personalInfo?.firstName} ${data?.personalInfo?.lastName}`
        );

        setError(null);
      }
    } catch (error) {
      setError(error.message);
      notifyWarn(
        error?.response?.data?.message || "An unexpected error occurred"
      );
      console.error("Error", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit button click
  const handleEdit = () => {
    console.log("Edit button clicked");
    setAddEmployeeModal(true);
  };

  const handleView = () => {
    setViewProfileData(profileData);
    setViewProfileEmpPublicId(profileData?.personalInfo?.publicId);
    setProfileModal(true);
  };

  const closeViewProfileModal = () => {
    setProfileModal(false);
  };

  // Close the modal
  const closeAddEmployeeModal = () => {
    setAddEmployeeModal(false);
  };

  // Close with warning (if needed)
  const closeAddEmployeeModalWarning = () => {
    setConfirmTitle("Close Form?");
    setConfirmMsg("Any unsaved changes will be lost.");
    setConfirmBtn("Yes, Close");
    setNotifyWarnModal(true);
  };

  const handleNotifyWarnModalConfirm = () => {
    setNotifyWarnModal(false);
    setAddEmployeeModal(false);
  };

  const handleNotifyWarnModalClose = () => {
    setNotifyWarnModal(false);
  };

  // Handle submit after edit
  const handleOnSubmit = (data) => {
    // Update profile data if needed
    closeAddEmployeeModal();
    setNotifySuccessModal(true);
    setSubmitTitle("Profile Updated Successfully");
    setSubmitMsg("");
    setSubmitBtn("OK");
    getItemsMaster();
  };

  const handleOnSubmitInvited = () => {
    closeAddEmployeeModal();
    setNotifySuccessModal(true);

    setSubmitTitle("Profile Details Saved Successfully!");
    setSubmitMsg(
      "Profile details have been saved successfully, and the invitation has been sent."
    );
    setSubmitBtn("");

    getItemsMaster();
  };

  const handleOnUpdateInvited = () => {
    closeAddEmployeeModal();
    setNotifySuccessModal(true);
    setSubmitTitle("Profile Details Updated Successfully!");
    setSubmitMsg(
      "The profile details have been updated successfully, and the invitation has been sent. You can view or manage them in the employee list."
    );
    setSubmitBtn("OK");
  };

  const handleSaveAndSubmitWhenUpdate = () => {
    setNotifySuccessModal(true);
    setSubmitTitle("Profile Details Updated Successfully!");
    setSubmitMsg("The profile details have been updated successfully.");
    setSubmitBtn("OK");
  };

  const notifySuccessModalClose = () => {
    setNotifySuccessModal(false);
  };

  // Handle draft submission
  const handleDraftSubmit = (data) => {
    setSubmitTitle("Profile Data Drafted Successfully");
    setSubmitMsg("");
    setSubmitBtn("OK");
    getItemsMaster();
    closeAddEmployeeModal();
    setNotifySuccessModal(true);
  };

  // Handle save and exit
  const handleSaveAndExitSubmit = () => {
    setSubmitTitle("Profile Data Saved Successfully");
    setSubmitMsg("");
    setSubmitBtn("OK");
    setNotifySuccessModal(true);
    closeAddEmployeeModal();
    getItemsMaster();
  };

  // Get updated employee counts
  const getNewEmployeeCount = () => {
    console.log("Getting updated employee counts");
    // Implement count fetching logic here if needed
    return Promise.resolve();
  };

  // Prepare fields array for the grid layout
  const profileFields = profileData
    ? [
        { label: t("First Name"), value: profileData?.personalInfo?.firstName },
        {
          label: t("First Name (Arabic)"),
          value: profileData?.personalInfo?.firstNameArb,
        },
        { label: t("Last Name"), value: profileData?.personalInfo?.lastName },
        {
          label: t("Last Name (Arabic)"),
          value: profileData?.personalInfo?.lastNameArb,
        },
        { label: t("Gender"), value: profileData?.personalInfo?.gender },
        {
          label: t("Citizen"),
          value:
            profileData?.personalInfo?.citizen === "true"
              ? "Saudi"
              : profileData?.personalInfo?.nationalityInfo?.name,
        },

        {
          label: t("Personal Email"),
          value: profileData?.personalInfo?.personalEmail,
        },
        { label: t("Email"), value: profileData?.personalInfo?.email },
        { label: t("Phone"), value: profileData?.personalInfo?.phone },
        {
          label: t("Branch"),
          value: profileData?.employementInfo?.branchInfo?.name,
        },
        {
          label: t("Reporting To"),
          value: (() => {
            const info = profileData?.employementInfo?.reportingToInfo;
            const name = info?.firstName || "";
            const lastName = info?.lastName || "";
            const email = info?.email || "";
            if (name && lastName && email)
              return `${name} ${lastName} (${email})`;
            if (name && email) return `${name} (${email})`;
            if (name) return name;
            if (email) return email;
            return null;
          })(),
        },
        {
          label: t("Role"),
          value: profileData?.employementInfo?.roleInfo?.role,
        },
        {
          label: t("Job Title"),
          value: profileData?.employementInfo?.jobTitleInfo?.name,
        },
        {
          label: t("Job Grade"),
          value: profileData?.employementInfo?.jobGradeInfo?.name,
        },
        {
          label: t("Designation"),
          value: profileData?.employementInfo?.designationInfo?.name,
        },
        {
          label: t("Department"),
          value: profileData?.employementInfo?.departmentInfo?.name,
        },
      ]
    : [];

  // Loading UI component
  const LoadingScreen = () => (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1A6D73]"></div>
    </div>
  );

  // Error UI component
  const ErrorScreen = () => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
      {t("Failed to load profile data. Please try again later.")}
    </div>
  );

  // Profile content UI component
  const ProfileContent = () => (
    <div className="w-full p-4 sm:p-6">
      <div className="w-full mx-auto bg-white border rounded-xl border-[#F2F4F7]">
        {/* Header */}
        <div className="flex justify-between items-center p-4 sm:p-6 border-b border-[#EAECF0]">
          <h2 className="text-[24px] font-bold leading-[28px] text-left text-[#04080B]">
            {t("Profile Information")}
          </h2>

          <div className="flex flex-col gap-1 justify-center align-middle">
            <button
              onClick={handleEdit}
              className="flex items-center gap-2 px-3 py-2 rounded-md text-white bg-[#B99A51] hover:bg-[#156268] text-sm"
            >
              <Edit size={16} />
              {t("Edit")}
            </button>
            <button
              onClick={handleView}
              className="flex items-center gap-2 px-3 py-2 rounded-md text-white bg-[#B99A51] hover:bg-[#156268] text-sm"
            >
              <Eye size={16} />
              {t("View Profile")}
            </button>
          </div>
        </div>

        {/* Profile Content */}
        <div className="p-4 sm:p-6">
          <p className="text-[#475467] mb-6 text-sm sm:text-base">
            {t("Easily access and update your profile information.")}
          </p>

          {/* Profile Picture and Employee ID Row */}
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            {/* Left: Profile Picture */}
            <div className="flex items-center gap-4">
              {profileData?.personalInfo?.profilePicture?.fileUrl ? (
                <img
                  src={profileData.personalInfo.profilePicture.fileUrl}
                  alt={`${profileData?.personalInfo?.firstName} ${profileData?.personalInfo?.lastName}`}
                  className="w-24 h-24 rounded-full object-cover border-2 border-[#B99A51]"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-[#F9FAFB] border-2 border-[#EAECF0] flex items-center justify-center">
                  <span className="text-[#98A2B3] text-xs text-center px-2">
                    {t("No Profile Photo Uploaded")}
                  </span>
                </div>
              )}
              <div>
                <h3 className="text-lg font-semibold text-[#04080B]">
                  {profileData?.personalInfo?.firstName}{" "}
                  {profileData?.personalInfo?.lastName}
                </h3>
                <p className="text-sm text-[#475467]">
                  {profileData?.employementInfo?.jobTitleInfo?.name ||
                    t("Job Title")}
                </p>
              </div>
            </div>

            {/* Right: Employee ID */}
            <div className="p-4 bg-[#F0F9FF] border border-[#B3E5FC] rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-[#475467]">
                  {t("Employee ID")}:
                </span>
                <span className="text-[#04080B] font-semibold text-lg">
                  {profileData?.employementInfo?.employeeId ||
                    t("Not assigned")}
                </span>
              </div>
            </div>
          </div>

          {/* 3-column grid for profile fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6">
            {profileFields.map((field, index) => (
              <div
                key={index}
                className="bg-[#F9FAFB] border border-[#F2F4F7] rounded-lg p-4"
              >
                <p className="text-sm font-medium text-[#475467] mb-1">
                  {field.label}
                </p>
                <p className="text-[#04080B] font-medium">
                  {field.value || t("Not provided")}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        {isLoading ? (
          <LoadingScreen />
        ) : error ? (
          <ErrorScreen />
        ) : (
          <ProfileContent />
        )}
      </div>

      {/* Add Employee Modal for editing */}
      {addEmployeeModal && (
        <AddEmployeeModal
          currentEmployeeEditing={true}
          handleOnSubmit={handleOnSubmit}
          handleOnSubmitInvited={handleOnSubmitInvited}
          handleOnUpdateInvited={handleOnUpdateInvited}
          isOpen={addEmployeeModal}
          onClose={closeAddEmployeeModal}
          onCloseWarning={closeAddEmployeeModalWarning}
          empPublicId={profileData?.personalInfo?.publicId}
          invitePending={profileData?.personalInfo?.userInvitation === "false"}
          handleGetTableData={getItemsMaster}
          handleDraftSubmit={handleDraftSubmit}
          handleSaveSubmit={handleSaveAndExitSubmit}
          getEmployeeStats={getNewEmployeeCount}
          getAdditionalStats={getNewEmployeeCount}
          handleSaveAndSubmitWhenUpdate={handleSaveAndSubmitWhenUpdate}
        />
      )}

      {profileModal && (
        <EmployeeModal
          currentEmployeeEditing={true}
          isOpen={profileModal}
          onClose={closeViewProfileModal}
          empPublicId={viewProfileEmpPublicId}
          employeeGeneralInfo={viewProfileData}
          handleGetTableData={getItemsMaster}
        />
      )}

      {notifySuccessModal && (
        <DeleteConfirmationModal
          icon={<Tick />}
          isOpen={notifySuccessModal}
          headline={submitTitle}
          paraText={submitMsg}
          confirmText={submitBtn}
          isConfirm={true}
          onConfirm={notifySuccessModalClose}
          onCancel={notifySuccessModalClose}
          onClose={notifySuccessModalClose}
        />
      )}

      {notifyWarnModal && (
        <DeleteConfirmationModal
          icon={<ExclaimRed />}
          isOpen={notifyWarnModal}
          headline={confirmTitle}
          headlineData={""}
          paraText={confirmMsg}
          paraTextData={""}
          buttonText="cancel"
          confirmText={confirmBtn}
          isDelete={true}
          onConfirm={handleNotifyWarnModalConfirm}
          onCancel={handleNotifyWarnModalClose}
          onClose={handleNotifyWarnModalClose}
        />
      )}
    </div>
  );
};

export default MyProfile;
